import type { StockStatus, StorageLocation } from "@sebas/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type PantryItem = {
  id: string;
  name: string;
  normalizedName: string;
  quantity: number | null;
  unit: string;
  location: StorageLocation;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Ingredient = {
  id: string;
  noteId: string;
  position: number;
  name: string;
  normalizedName: string;
  quantity: number | null;
  unit: string;
  note: string | null;
  status: StockStatus;
  inFridge: { id: string; name: string; quantity: number | null; unit: string } | null;
};

export type IngredientsResponse = {
  ingredients: Ingredient[];
  summary: { total: number; ready: number; missing: string[] };
};

export type CookResult = {
  items: PantryItem[];
  summary: { deducted: string[]; skipped: string[]; usedUp: string[] };
};

type ItemInput = {
  name?: string;
  quantity?: number | null;
  unit?: string;
  location?: StorageLocation;
  expiresAt?: string | null;
};

export const pantryKeys = {
  all: ["pantry"] as const,
  list: (teamId: string | null) => ["pantry", "list", teamId] as const,
};

export const recipeKeys = {
  ingredients: (noteId: string) => ["recipe", "ingredients", noteId] as const,
};

/**
 * The fridge for a scope. One inventory per account, so every fridge note
 * shares this cache entry and they never drift apart on screen.
 */
export function useFridge(teamId: string | null = null) {
  return useQuery({
    queryKey: pantryKeys.list(teamId),
    queryFn: () =>
      api<{ items: PantryItem[] }>(`/v1/pantry${teamId ? `?teamId=${teamId}` : ""}`),
    select: (r) => r.items,
  });
}

/**
 * Stock is derived from the fridge, so any change to one invalidates the other.
 * Every mutation below goes through this rather than listing keys by hand.
 */
function useKitchenMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pantryKeys.all });
      qc.invalidateQueries({ queryKey: ["recipe"] });
    },
  });
}

export function useAddFridgeItem(teamId: string | null = null) {
  return useKitchenMutation((input: ItemInput) =>
    api<{ item: PantryItem; merged: boolean }>("/v1/pantry", {
      method: "POST",
      body: JSON.stringify({ ...input, teamId }),
    }),
  );
}

export function useUpdateFridgeItem() {
  return useKitchenMutation(({ id, ...patch }: ItemInput & { id: string }) =>
    api<{ item: PantryItem }>(`/v1/pantry/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  );
}

export function useDeleteFridgeItem() {
  return useKitchenMutation((id: string) => api<void>(`/v1/pantry/${id}`, { method: "DELETE" }));
}

export function useIngredients(noteId: string) {
  return useQuery({
    queryKey: recipeKeys.ingredients(noteId),
    queryFn: () => api<IngredientsResponse>(`/v1/notes/${noteId}/ingredients`),
  });
}

type IngredientInput = { name?: string; quantity?: number | null; unit?: string; note?: string | null };

export function useAddIngredient(noteId: string) {
  return useKitchenMutation((input: IngredientInput) =>
    api<{ ingredient: Ingredient }>(`/v1/notes/${noteId}/ingredients`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateIngredient(noteId: string) {
  return useKitchenMutation(({ id, ...patch }: IngredientInput & { id: string }) =>
    api<{ ingredient: Ingredient }>(`/v1/notes/${noteId}/ingredients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  );
}

export function useDeleteIngredient(noteId: string) {
  return useKitchenMutation((id: string) =>
    api<void>(`/v1/notes/${noteId}/ingredients/${id}`, { method: "DELETE" }),
  );
}

export function useCookRecipe(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) =>
      api<CookResult>(`/v1/notes/${noteId}/cook`, {
        method: "POST",
        body: JSON.stringify({ force }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pantryKeys.all });
      qc.invalidateQueries({ queryKey: ["recipe"] });
    },
  });
}
